"use client";

import Image from "next/image";
import { ArrowUp, CircleCheck, Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { useBookCallTriggerProps } from "@/lib/use-book-call-trigger-props";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { AiAgentsSubpageCopy } from "./ai-agents-page-copy";

type CcAgentComposerProps = {
  copy: AiAgentsSubpageCopy["composer"];
  sendLabel: string;
  /** Tells the hero app which agent is working, to light it up in the sidebar. */
  onAgentChange?: (agentId: string) => void;
};

// Steps tick in one by one, then the result shows; the next example follows.
const STEP_MS = 650;
const HOLD_MS = 3200;

/**
 * The hero's agent prompt after twin.so: a sentence describing the job with
 * the tools as small logo pills, example chips underneath and a run log
 * where the agent works through the steps. Examples rotate on their own
 * until the visitor picks one; the send button opens the booking popup.
 */
export function CcAgentComposer({ copy, sendLabel, onAgentChange }: CcAgentComposerProps) {
  const reduced = useCcPrefersReducedMotion();
  const triggerProps = useBookCallTriggerProps({ bookCallVariant: "ai-agents" });
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const example = copy.examples[index];

  useEffect(() => {
    onAgentChange?.(example.agentId);
  }, [example.agentId, onAgentChange]);

  useEffect(() => {
    if (reduced || picked) return;
    const root = rootRef.current;
    let visible = true;
    const observer = root
      ? new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
        })
      : null;
    if (root && observer) observer.observe(root);
    const total = example.steps.length * STEP_MS + HOLD_MS + 900;
    const timer = window.setTimeout(() => {
      if (visible && document.visibilityState === "visible") {
        setIndex((current) => (current + 1) % copy.examples.length);
      }
    }, total);
    return () => {
      window.clearTimeout(timer);
      observer?.disconnect();
    };
  }, [index, picked, reduced, example.steps.length, copy.examples.length]);

  return (
    <div ref={rootRef} className="cc-agent-composer">
      <div className="cc-agent-composer__card">
        <span className="cc-agent-composer__label">{copy.label}</span>
        <p key={example.id} className="cc-agent-composer__prompt" aria-live="polite">
          {example.parts.map((part, partIndex) => (
            <span key={partIndex} className="cc-agent-composer__part" style={{ "--cc-ag-i": partIndex } as CSSProperties}>
              {"tool" in part && part.tool ? (
                <span className="cc-agent-pill">
                  <Image src={part.logo} alt="" width={14} height={14} unoptimized />
                  {part.tool}
                </span>
              ) : (
                "text" in part && part.text
              )}
            </span>
          ))}
          <i className="cc-agent-composer__caret" aria-hidden="true" />
        </p>
        <div className="cc-agent-composer__bar">
          <span className="cc-agent-composer__examples-label">{copy.examplesLabel}</span>
          <div className="cc-agent-composer__chips">
            {copy.examples.map((item, itemIndex) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={itemIndex === index}
                onClick={() => {
                  setPicked(true);
                  setIndex(itemIndex);
                }}
              >
                {item.chip}
              </button>
            ))}
          </div>
          <button type="button" className="cc-agent-composer__send" aria-label={sendLabel} {...triggerProps}>
            <ArrowUp size={18} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <div key={example.id} className="cc-agent-run" aria-hidden="true" data-reduced={reduced ? "true" : undefined}>
        <span className="cc-agent-run__head" style={{ "--cc-ag-done": `${example.steps.length * STEP_MS}ms` } as CSSProperties}>
          <span className="cc-agent-run__working">
            <Loader2 size={13} strokeWidth={2.4} />
            {copy.runTitle}
          </span>
          <span className="cc-agent-run__done">
            <CircleCheck size={13} strokeWidth={2.4} />
            {copy.doneTitle}
          </span>
        </span>
        <ol>
          {example.steps.map((stepText, stepIndex) => (
            <li key={stepText} style={{ "--cc-ag-delay": `${stepIndex * STEP_MS + 300}ms` } as CSSProperties}>
              <CircleCheck size={14} strokeWidth={2.2} />
              {stepText}
            </li>
          ))}
        </ol>
        <p className="cc-agent-run__result" style={{ "--cc-ag-delay": `${example.steps.length * STEP_MS + 400}ms` } as CSSProperties}>
          {example.result}
        </p>
      </div>
    </div>
  );
}
